// EP-150 (App Review 5.1.1v): the erase date shown on the sign-in page after
// a deletion request.
//
// It travels in sessionStorage, not in router state. Signing out makes
// ProtectedRoute redirect to /login by itself, and that redirect carries no
// state, so with router state the page showed the date only when its own
// navigation happened to land last. Found in the browser on 2026-09-28.
//
// The notice is for the person who has just deleted, on the page they land
// on. It is dropped at the first sign-in attempt and after 15 minutes, so
// the next person at the same browser is not told about someone else's
// account. Found in the same browser pass.
import { formatDate, DEFAULT_CALENDAR, DEFAULT_DATE_FORMAT, DEFAULT_TIMEZONE } from '@/lib/date'

export interface DeletionDateDisplay {
  timezone?: string
  date_format?: string
  calendar?: string
}

export interface DeletionNotice {
  scope: 'account' | 'user'
  scheduledAt: string
  // How the account shows dates. The sign-in page has no session to read
  // it from, so whoever knows it hands it over.
  display?: DeletionDateDisplay
}

const STORAGE_KEY = 'flowrent_deletion_notice'
export const DELETION_NOTICE_LIFETIME_MS = 15 * 60 * 1000

export function saveDeletionNotice(notice: DeletionNotice): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...notice, savedAt: Date.now() }))
  } catch {
    // Storage can be unavailable (private mode, a full quota). The notice is
    // a courtesy: the sign-in page still explains the state on sign-in.
  }
}

export function readDeletionNotice(): DeletionNotice | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<DeletionNotice> & { savedAt?: unknown }
    const scopeOk = parsed.scope === 'account' || parsed.scope === 'user'
    if (!scopeOk || typeof parsed.scheduledAt !== 'string') return null
    const age = typeof parsed.savedAt === 'number' ? Date.now() - parsed.savedAt : Infinity
    if (age < 0 || age > DELETION_NOTICE_LIFETIME_MS) {
      clearDeletionNotice()
      return null
    }
    const display =
      parsed.display && typeof parsed.display === 'object' ? (parsed.display as DeletionDateDisplay) : undefined
    return { scope: parsed.scope as DeletionNotice['scope'], scheduledAt: parsed.scheduledAt, display }
  } catch {
    return null
  }
}

/**
 * The erase date, in the account's own format, timezone and calendar when
 * they were handed over, in the defaults otherwise.
 */
export function formatDeletionDate(iso: string, display?: DeletionDateDisplay): string {
  const calendar = display?.calendar === 'buddhist' ? 'buddhist' : DEFAULT_CALENDAR
  const format = (['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'] as const).find((f) => f === display?.date_format)
  let timezone = DEFAULT_TIMEZONE
  if (display?.timezone) {
    try {
      new Intl.DateTimeFormat('en', { timeZone: display.timezone })
      timezone = display.timezone
    } catch {
      // Not a timezone the browser knows. The default stands.
    }
  }
  return formatDate(iso, { format: format ?? DEFAULT_DATE_FORMAT, timezone, calendar })
}

export function clearDeletionNotice(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
